const PrintSummaryTotals = (summaryData, reciptDetails) => {
  if (!summaryData || !summaryData.data2) return;

  const { data2, totalSummaryAmount, fromDate, response } = summaryData;

  const homeDelivery = parseFloat(data2.homeDeliveryTotal || 0);
  const takeaway = parseFloat(data2.takeawayTotal || 0);
  const dining = parseFloat(data2.diningTotal || 0);

  const totalCash = parseFloat(data2.totalCashCollectedAmount || 0);
  const totalUPI = parseFloat(data2.totalUPICollectedAmount || 0);
  const totalCard = parseFloat(data2.totalCardCollectedAmount || 0);

  const apiTotalCGST = (response || []).reduce(
    (sum, i) => sum + parseFloat(i.tax_cgst || 0),
    0
  );

  const apiTotalSGST = (response || []).reduce(
    (sum, i) => sum + parseFloat(i.tax_sgst || 0),
    0
  );

  const subtotalAmount = parseFloat(totalSummaryAmount || 0);

  const getGstType = () => {
    const value = String(
      reciptDetails?.gstType ||
        reciptDetails?.gst_type ||
        reciptDetails?.tax_type ||
        reciptDetails?.taxType ||
        ""
    )
      .toLowerCase()
      .trim();

    if (
      value === "incl" ||
      value === "inclusive" ||
      value === "included"
    ) {
      return "incl";
    }

    if (
      value === "excl" ||
      value === "exclusive" ||
      value === "excluded"
    ) {
      return "excl";
    }

    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");

      const userID =
        user?.userid ||
        user?.userId ||
        user?.fk_userid ||
        user?.id ||
        "";

      const raw = sessionStorage.getItem(`pos_org_tax_config_${userID}`);
      const storedTaxConfig = raw ? JSON.parse(raw) : {};

      const storedValue = String(
        storedTaxConfig?.gstType || storedTaxConfig?.gst_type || ""
      )
        .toLowerCase()
        .trim();

      if (
        storedValue === "incl" ||
        storedValue === "inclusive" ||
        storedValue === "included"
      ) {
        return "incl";
      }

      if (
        storedValue === "excl" ||
        storedValue === "exclusive" ||
        storedValue === "excluded"
      ) {
        return "excl";
      }
    } catch (error) {
      console.error("Summary GST config parse error:", error);
    }

    return "incl";
  };

  const getTaxRate = (value, fallback = 2.5) => {
    const num = Number(value || 0);
    return Number.isFinite(num) && num > 0 ? num : fallback;
  };

  const gstType = getGstType();
  const isInclusiveGst = gstType === "incl";

  const cgstRate = getTaxRate(
    reciptDetails?.taxCgst ||
      reciptDetails?.tax_cgst ||
      reciptDetails?.cgstRate,
    2.5
  );

  const sgstRate = getTaxRate(
    reciptDetails?.taxSgst ||
      reciptDetails?.tax_sgst ||
      reciptDetails?.sgstRate,
    2.5
  );

  const totalGstRate = cgstRate + sgstRate;

  const includedTotalTax =
    isInclusiveGst && totalGstRate > 0
      ? subtotalAmount - subtotalAmount / (1 + totalGstRate / 100)
      : 0;

  const displayCGST = isInclusiveGst
    ? includedTotalTax * (cgstRate / totalGstRate)
    : apiTotalCGST;

  const displaySGST = isInclusiveGst
    ? includedTotalTax * (sgstRate / totalGstRate)
    : apiTotalSGST;

  // Important:
  // API totalSummaryAmount already contains correct total.
  // So do not add CGST + SGST again in either inclusive or exclusive case.
  const finalTotal = subtotalAmount;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const d = new Date(dateStr);

    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const formattedReceiptHTML = `
  <html>
  <head>
    <style>
      body { font-family: Arial, sans-serif; }

      .container {
        max-width: 600px;
        margin: 0 auto;
        padding: 0 4px;
      }

      .header {
        text-align: center;
        border-bottom: 1px dashed #000;
        margin-top: 10px;
      }

      .header2 {
        border-top: 1px dashed #000;
        border-bottom: 1px dashed #000;
        padding: 5px 0;
        text-align: center;
      }

      .section {
        border-top: 1px dashed #000;
        border-bottom: 1px dashed #000;
        padding: 6px 0;
        font-size: 10px;
        margin-top: 10px;
      }

      .sectionTitle {
        text-align: center;
        font-weight: 600;
        margin-bottom: 5px;
        font-size: 11px;
      }

      .textSizeLarge { font-size: 16px; margin: 1px; }
      .textSizeMid { font-size: 11px; margin: 2px; }
    </style>
  </head>

  <body>
    <div class="container">

      <!-- HEADER -->
      <div class="header">
        <p class="textSizeLarge"><b>${reciptDetails?.business_name || ""}</b></p>
        <p class="textSizeMid">${reciptDetails?.address || ""}</p>
        <p class="textSizeMid">PH: ${reciptDetails?.phone_no || ""}</p>
        <p class="textSizeMid">GST: ${reciptDetails?.gst || ""}</p>
        <p class="textSizeLarge"><b>SUMMARY TOTALS</b></p>
      </div>

      <!-- DATE -->
      <div class="header2">
        <p class="textSizeMid">Date: ${formatDate(fromDate)}</p>
      </div>

      <!-- POS SALES -->
      <div class="section">
        <div class="sectionTitle">POS SALES</div>

        <div style="display:flex; justify-content:space-between;">
          <span>Home Delivery</span>
          <span>₹ ${homeDelivery.toFixed(2)}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>Takeaway</span>
          <span>₹ ${takeaway.toFixed(2)}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>Dining</span>
          <span>₹ ${dining.toFixed(2)}</span>
        </div>
      </div>

      <!-- TAX DETAILS -->
      <div class="section">
        <div class="sectionTitle">
          TAX DETAILS ${isInclusiveGst ? "(GST INCLUDED)" : "(GST EXTRA)"}
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>Total Sales</span>
          <span>₹ ${subtotalAmount.toFixed(2)}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>${isInclusiveGst ? "CGST Included" : "CGST"}</span>
          <span>₹ ${displayCGST.toFixed(2)}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>${isInclusiveGst ? "SGST Included" : "SGST"}</span>
          <span>₹ ${displaySGST.toFixed(2)}</span>
        </div>

        <div style="border-top:1px dashed #000; margin:6px 0;"></div>

        <div style="display:flex; justify-content:space-between; font-weight:600;">
          <span>Final Total</span>
          <span>₹ ${finalTotal.toFixed(2)}</span>
        </div>
      </div>

      <!-- PAYMENT SUMMARY -->
      <div class="section">
        <div class="sectionTitle">PAYMENT SUMMARY</div>

        <div style="display:flex; justify-content:space-between;">
          <span>Cash</span>
          <span>₹ ${totalCash.toFixed(2)}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>UPI</span>
          <span>₹ ${totalUPI.toFixed(2)}</span>
        </div>

        <div style="display:flex; justify-content:space-between;">
          <span>Card</span>
          <span>₹ ${totalCard.toFixed(2)}</span>
        </div>

        <div style="border-top:1px dashed #000; margin:6px 0;"></div>

        <div style="display:flex; justify-content:space-between; font-weight:600;">
          <span>Total Collected</span>
          <span>₹ ${(totalCash + totalUPI + totalCard).toFixed(2)}</span>
        </div>
      </div>

    </div>
  </body>
  </html>
  `;

  const printWindow = window.open("", "_blank");
  printWindow.document.open();
  printWindow.document.write(formattedReceiptHTML);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

export default PrintSummaryTotals;